import {
    EVENTBRITE_CATEGORY_TO_REFINEMENT,
    FORMATTED_SUBCATEGORY_MAP,
    SEASONAL_EVENTBRITE_CATEGORY,
    SUBCATEGORY_REFINEMENT_TO_KEY,
} from '@eventbrite/categories';
import { gettext } from '@eventbrite/i18n';
import { SeasonalBannerProps } from './types';

const LISTING_PAGE_EXPERIMENTS_LAYER = 'listing_page_experiments';

// Statsig's Events

export const STATSIG_EVENT_NAMES = {
    LISTING_APP_PAGE_VIEW: 'Listing - App - View - Page',
    LISTING_CONVERSION_BAR_CTA_CLICK: 'Listing - conversionBar - Click - CTA',
    LISTING_TICKET_SELECTION_VIEW:
        'Listing - Conversion Bar - Ticket Selection - View',
    LISTING_ALL_EVENT_DETAILS_VIEW:
        'Listing - All - Click - View All Event Details',
    LISTING_TICKET_SELECTION_CLICK:
        'Listing - Conversion Bar - Ticket Selection - Click',
    CHILD_EVENTS_DROPDOWN:
        'Listing - Select a date - Change - Child Events Dropdown',
    MORE_OPTIONS_BUTTON:
        'Listing - Select a date - Click - More Options Button',
    MORE_OPTIONS_CARD: 'Listing - Select a date - Click - More Options Card',
    CAROUSEL_CARD: 'Listing - Select a date - Click - Carousel card',
    LISTING_MORE_ORGANIZER_EVENT_CLICK:
        'Listing - All - Click - More Organizer Event',
    LISTING_MORE_ORGANIZER_EVENT_LIKE:
        'Listing - All - Click - More Organizer Event Like',
    LISTING_MORE_ORGANIZER_EVENT_SHARE:
        'Listing - All - Click - More Organizer Event Share',
    LISTING_RELATED_EVENT_CLICK: 'Listing - All - Click - Related Event',
    LISTING_RELATED_EVENT_LIKE: 'Listing - All - Click - Related Event Like',
    LISTING_RELATED_EVENT_SHARE: 'Listing - All - Click - Related Event Share',
    LISTING_AD_BANNER_CLICK: 'Listing - All - Click - Ad Banner CTA',
    LISTING_NIGHTLIFE_BANNER_CLICK:
        'Listing - All - Click - Nightlife Banner CTA',
    LISTING_NYE_TOUCHPOINT_CLICK: 'Listing - All - Click - NYE Touchpoint CTA',
    LISTING_SUBCATEGORY_CTA_CLICK:
        'Listing - Expired Event Page - Click - Category Browse Modal CTA',
    LISTING_FOLLOW_CLICK: 'Listing - All - Click - Follow',
    LISTING_UNFOLLOW_CLICK: 'Listing - All - Click - Unfollow',
};

// Heap ID's
export const LISTING_HEAP_IDS = {
    RELATED_EVENTS_STRING: 'Related Events',
    CAROUSEL_CARD: 'Hero Content - Carousel card - Change',
    CHILD_EVENTS_DROPDOWN: 'Listing - Date - Child Events Dropdown - Click',
    CHECKOUT_BUTTON: 'Conversion Bar - Checkout Button',
    CREATOR_TOP_BANNER_EDIT: 'Creator Top Banner - Edit',
    CTA_RSVP_MAYBE: 'Listing - Conversion Bar - RSVP Maybe - Click',
    CTA_RSVP_NO: 'Listing - Conversion Bar - RSVP No - Click',
    DESCRIPTIVE_ORGANIZER:
        'Listing - Descriptive Organizer Panel - Follow - Click',
    EXPLORE_SIMILAR_EVENTS:
        'Listing - Conversion Bar - Explore Similar Events - Click',
    FAQS_DROPDOWN: 'FAQs - Dropdown',
    FOLLOW_BUTTON: 'Listing - Follow - Button - Click',
    HERO_VIDEO: 'Promo Video - Play',
    HIDE_MAP: 'Listing - Hide Map - Button - Click',
    IMAGE_CATEGORY_CLICK:
        'Listing - Expired Event Page - Click - Category Browse',
    IMAGE_CATEGORY_VIEW:
        'Listing - Expired Event Page - View - Category Browse',
    LEFT_ARROW_HERO_CAROUSEL: 'Hero Content - Hero Carousel - Left',
    MAP_DRIVE: 'Listing - Map - Drive - Click',
    MAP_WALK: 'Listing - Map - Walk - Click',
    MAP_BUS: 'Listing - Map - Bus - Click',
    MAP_BIKE: 'Listing - Map - Bike - Click',
    MAP_ZOOM_CHANGE: 'Listing - Map - Zoom - Change',
    MORE_COLLECTIONS_THAT_FEATURE_EVENT:
        'More Collections That Feature This Event - View more collections Button',
    MORE_OPTIONS_BUTTON:
        'Hero Content - More options button in Repeating events - Click',
    MORE_OPTIONS_CARD:
        'Hero Content - More options last card in Repeating events - Click',
    MORE_OPTIONS_DATE:
        'Hero Content - Change date confirmation in Calendar - Click',
    ONLINE_EVENT_ACCESS_BUTTON:
        'Listing - Online Location - Access the Event - Click',
    ORGANIZER_FACEBOOK:
        'Listing - Organizer Panel - Organizer Facebook - Click',
    ORGANIZER_NAME: 'Listing - Organizer Panel - Organizer Name - Click',
    ORGANIZER_TWITTER: 'Listing - Organizer Panel - Organizer Twitter - Click',
    ORGANIZER_WEBSITE: 'Listing - Organizer Panel - Organizer Website - Click',
    PART_OF_A_COLLECTION:
        'Hero Content - Part of /Featured in Collection/s button',
    RECCOMENDATIONS_VIEW: 'PersonalizedRelatedEventsView',
    RIGHT_ARROW_HERO_CAROUSEL: 'Hero Content - Hero Carousel - Right',
    SHARE_EVENT: 'Listing - Hero - Share - Click',
    SHOW_MAP: 'Listing - Show Map - Button - Click',
    SIMPLIFIED_ORGANIZER:
        'Listing - Simplified Organizer Panel - Follow - Click',
    UNFOLLOW_BUTTON: 'Listing - Following - Button - Click',
    FOLLOW_ORGANIZER: 'FollowOrganizerClick',
    FOLLOW_ORGANIZER_SUCCESS: 'FollowOrganizerSuccessful',
    UNFOLLOW_ORGANIZER: 'UnfollowOrganizerClick',
    UNFOLLOW_ORGANIZER_SUCCESS: 'UnfollowOrganizerSuccessful',
};

export const LISTING_HEAP_PAGE_AREA_IDS = {
    SIMPLIFIED_ORGANIZER: 'listing-simplified-organizer',
    DESCRIPTIVE_ORGANIZER: 'listing-descriptive-organizer',
    HERO_CONTENT: 'listing-hero-content',
    ORGANIZER_PANEL: 'listing-organizer-panel',
    REMIND_ME_MODAL: 'listing-remind-me-modal',
    EXPIRED_EVENTS_PAGE: 'listing-expired-events-page',
    HYDRATE_FOLLOW_STATUS: 'listing-hydrate-follow-status',
};

// Seasonal Banner constants
export const SEASONAL_BANNER_CONTENT: { [key: string]: SeasonalBannerProps } = {
    HALLOWEEN: {
        title: gettext('Trick or Treat Yourself'),
        subtitle: gettext(
            'Nothing like a little hocus pocus to get into the Halloween spirit.',
        ),
        ctaLink: `/b/local/${
            EVENTBRITE_CATEGORY_TO_REFINEMENT[SEASONAL_EVENTBRITE_CATEGORY]
        }/${
            FORMATTED_SUBCATEGORY_MAP[
                SUBCATEGORY_REFINEMENT_TO_KEY[SEASONAL_EVENTBRITE_CATEGORY][
                    'halloweenhaunt'
                ]
            ]['refinement']
        }/?utm_source=listing_halloween_banner_click`,
        ctaText: gettext("Let's Go"),
    },
};

export const SALES_STATUS_ON_SALE = 'on_sale';

// Statsig's Multi Tickets Experiment
export const MULTI_TICKETS_ON_LISTINGS_PAGE_EXPERIMENT = {
    defaultValue: false,
    layer: LISTING_PAGE_EXPERIMENTS_LAYER,
    param: 'multi_ticket_selection',
};

// Statsig's Unifying Repeating Events Customer Experience Experiment
export const LISTING_PAGE_UNIFYING_REPEATING_EVENTS_CONSUMER_EXPERIMENT = {
    experiment: 'listing_unify_repeating_events_consumer_experience',
    defaultValue: false,
    param: 'parent_shows_child_info',
};

//Statsig's Avoid recharge dates on all events
export const LISTING_AVOID_RECHARGE_DATES_ON_ALL_EVENTS = {
    experimentName: 'listing_avoid_recharge_dates_on_all_events',
    param: 'avoid_recharge_event_dates',
    defaultValue: false,
};

//Statsig's Seasonal Ad Banner Visibility
export const LISTING_SEASONAL_AD_BANNER = {
    experimentName: 'halloween_promotion_on_listing_page',
    param: 'show_seasonal_ad_banner',
    defaultValue: false,
};

//Statsig's Subcategory Browse Modal Visibility on Expired/Past Events Page
export const LISTING_SUBCATEGORY_BROWSE = {
    experimentName: 'listings_subcategory_browse',
    param: 'show_subcategory_browse_modal',
    defaultValue: 'hide',
};

//Statsig's NYE/Nightlife Touchpoint Visibility
export const LISTING_NIGHTLIFE_NYE_TOUCHPOINT = {
    experimentName: 'nye_nightlife_touchpoint_on_listing_page',
    param: 'show_nye_nightlife_touchpoint',
    defaultValue: 'hide',
};

//Statsig's create quality trust signal
export const LISTING_CREATE_QUALITY_SIGNAL = {
    experimentName: 'creator_quality_trust_signal_listing_page_phase2',
    param: 'show_variant_type',
    defaultValue: 'control',
};

//Statsig's showing default profile picture
export const LISTING_DEFAULT_PROFILE_PICTURE = {
    experimentName: 'creator_quality_profile_picture_listing_page',
    param: 'show_variant_type',
    defaultValue: 'control',
};

// Statsig's Related Events Grid Experiment for Personalization Team
export const RELATED_EVENTS_GRID_EXPERIMENT = {
    experimentName: 'related_events_grid_listings',
    param: 'show_grid',
    defaultValue: false,
};

// Statsig's First Hierarchy Level Experiment for Listings Team
export const LISTING_FIRST_HIERARCHY_LEVEL_EXPERIMENT = {
    experimentName: 'listing_first_hierarchy_level',
    param: 'show_first_hierarchy_level',
    defaultValue: false,
};

/**
 * As part of improving consumer’s effort to ensuring experimentation data
 * consistency between Statsig and Heap, Browse & Explore will run a series
 * of A/A tests to validate what the discrepancy looks like today.
 */
export const AA_STABLE_ID_TEST = {
    experimentName: 'aa_stableid_test',
    param: 'variant_type',
    defaultValue: 'control',
};

export const AA_SESSION_ID_TEST = {
    experimentName: 'aa_sessionid_test',
    param: 'variant_type',
    defaultValue: 'control',
};

export const AA_USER_ID_TEST = {
    experimentName: 'aa_userid_test',
    param: 'variant_type',
    defaultValue: 'control',
};
